import { useRef, useState } from "react"
import { CalendarBlank, Folder, Package, Plus } from "@phosphor-icons/react"

import {
  Badge,
  Button,
  DateRangePicker,
  EmptyState,
  ExpandableCard,
  ExpandableCardContent,
  ExpandableCardHeader,
  InlineAlert,
  Input,
  PermissionTooltip,
} from "~/components/design-system/components"
import { usePermission } from "~/hooks/usePermission"
import { FilterScopeProvider } from "~/providers/FilterScopeProvider"
import { DateFormatType, formatDate, toUTCDateString } from "~/utils/datetime"
import { pluralize } from "~/utils/formatting"
import { cn } from "~/utils/helpers"
import { PACKAGE_NAME_MAX } from "~/utils/allocationPackageOps"
import { showErrorToast } from "~/utils/toaster"
import { CardTitle, PACKAGE_CARD_KEY } from "./PackageCardTitle"
import { DeletionImpact } from "./PackageDeletionImpact"
import {
  focusAndSelect,
  folderDisabledReason,
  PACKAGE_STATUS_META,
  packageWindowLabel,
  railWindowLabel,
  storesLabel,
} from "./packageDisplay"
import { folderNameError, packagesInFolder } from "./packageFolders"
import { FolderActionsMenu } from "./PackageMenus"
import { usePackageNavigator, usePageRequest } from "./packageNavigator"
import { InheritanceSource } from "./InheritanceSource"
import { STORES_SCOPE, StoresCardBody } from "./PackageStoresCard"
import { noteRunAfterCurrent } from "./setup/setupDrafts"
import type { PackageFolder, RangePackage } from "./types"
import { useAllocationMutations } from "./useAllocationData"

type SaveFolder = ReturnType<typeof useAllocationMutations>["savePackageFolder"]

const toDate = (day: string) => new Date(`${day}T00:00:00Z`)
const dateFieldLabel = (date: Date) => formatDate(date, DateFormatType.FullDateShort)

function FolderName({
  folder,
  siblings,
  nameRef,
  disabledReason,
  save,
}: {
  folder: PackageFolder
  siblings: PackageFolder[]
  nameRef: React.RefObject<HTMLInputElement | null>
  disabledReason: string | undefined
  save: SaveFolder
}) {
  const [value, setValue] = useState(folder.name)
  const { allowed } = usePermission("edit")
  const taken = new Set(
    siblings
      .filter((other) => other.id !== folder.id)
      .map((other) => other.name.trim().toLowerCase()),
  )
  const error = folderNameError(value, taken)
  const commit = () => {
    if (value.trim() === folder.name.trim()) return setValue(folder.name)
    if (error) return
    save.mutate(
      { op: { kind: "renameFolder", folderId: folder.id, name: value.trim() } },
      { onError: showErrorToast },
    )
  }
  return (
    <PermissionTooltip requires="edit" allowedTitle={disabledReason}>
      <div className="w-[320px]">
        <Input
          ref={nameRef}
          aria-label="Folder name"
          value={value}
          maxLength={PACKAGE_NAME_MAX}
          onChange={(event) => setValue(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur()
            if (event.key === "Escape") setValue(folder.name)
          }}
          disabled={!allowed || disabledReason !== undefined || save.isPending}
          className="heading-sm"
        />
        {error && value !== folder.name && (
          <p className="paragraph-xs mt-1 text-fg-negative-text">{error}</p>
        )}
      </div>
    </PermissionTooltip>
  )
}

function FolderDatesCard({
  folder,
  parent,
  disabledReason,
  save,
  onSelectFolder,
}: {
  folder: PackageFolder
  parent: PackageFolder | undefined
  disabledReason: string | undefined
  save: SaveFolder
  onSelectFolder: (folderId: string) => void
}) {
  const { allowed } = usePermission("edit")
  const following = parent !== undefined && folder.ownStartDate === null
  const current =
    folder.startDate && folder.cutoffDate
      ? { startDate: folder.startDate, cutoffDate: folder.cutoffDate }
      : null
  const commit = (window: { startDate: string; cutoffDate: string } | null) =>
    save.mutate(
      { op: { kind: "setFolderWindow", folderId: folder.id, window } },
      { onSuccess: (result) => noteRunAfterCurrent(result.run), onError: showErrorToast },
    )
  const locked = !allowed || disabledReason !== undefined || save.isPending
  return (
    <ExpandableCard
      className="shrink-0"
      persistKey={`${PACKAGE_CARD_KEY}.folder-dates`}
      defaultExpanded
    >
      <ExpandableCardHeader>
        <CalendarBlank size={16} className="text-fg-muted" />
        <CardTitle meta={packageWindowLabel(folder.startDate, folder.cutoffDate)}>Dates</CardTitle>
        {parent && (
          <Badge variant={following ? "neutral" : "violet"}>
            {following ? `From ${parent.name}` : "Own dates"}
          </Badge>
        )}
      </ExpandableCardHeader>
      <ExpandableCardContent className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <PermissionTooltip requires="edit" allowedTitle={disabledReason}>
            <div className="w-[280px]">
              <DateRangePicker
                startDate={current ? toDate(current.startDate) : undefined}
                endDate={current ? toDate(current.cutoffDate) : undefined}
                onApply={({ from, to }) => {
                  if (!from || !to) return
                  commit({ startDate: toUTCDateString(from), cutoffDate: toUTCDateString(to) })
                }}
                formatDate={dateFieldLabel}
                validateRange={(from, to) =>
                  from && to ? null : "Pick a start date and a replenishment cut-off."
                }
                align="start"
                disabled={locked}
              />
            </div>
          </PermissionTooltip>
          {parent && !following && (
            <Button
              variant="transparent"
              size="sm"
              requires="edit"
              allowedTitle={disabledReason ?? `Follow ${parent.name}'s dates again`}
              disabled={locked}
              onClick={() => commit(null)}
            >
              {`Use ${parent.name}'s dates`}
            </Button>
          )}
          {!parent && current && (
            <Button
              variant="transparent"
              size="sm"
              requires="edit"
              allowedTitle={disabledReason ?? "Packages that follow this folder stop replenishing"}
              disabled={locked}
              onClick={() => commit(null)}
            >
              Clear dates
            </Button>
          )}
        </div>
        {parent && following && current === null && (
          <div className="flex items-center gap-2">
            <span className="paragraph-sm text-fg-muted">{`${parent.name} has no dates yet`}</span>
            <Button variant="transparent" size="sm" onClick={() => onSelectFolder(parent.id)}>
              {`Set dates on ${parent.name}`}
            </Button>
          </div>
        )}
        <p className="paragraph-sm text-fg-muted">
          Seasonal and clearance packages without dates of their own follow these, and stop
          replenishing until this folder has dates. NOOS packages stay always open.
        </p>
      </ExpandableCardContent>
    </ExpandableCard>
  )
}

function FolderStoresCard({
  folder,
  parent,
  packageCount,
  stagedReason,
  disabledReason,
  save,
}: {
  folder: PackageFolder
  parent: PackageFolder | undefined
  /** Why Apply to all waits: a package inside has staged store edits. */
  stagedReason: string | undefined
  packageCount: number
  disabledReason: string | undefined
  save: SaveFolder
}) {
  const [picked, setPicked] = useState<Set<string> | null>(null)
  const saved = new Set(folder.storeIds)
  const assignedIds = picked ?? saved
  const following = parent !== undefined && !folder.ownStores
  const changed =
    picked !== null &&
    (picked.size !== saved.size || [...picked].some((storeId) => !saved.has(storeId)))
  const apply = () =>
    save.mutate(
      { op: { kind: "setFolderStores", folderId: folder.id, storeIds: [...assignedIds] } },
      {
        onSuccess: (result) => {
          setPicked(null)
          noteRunAfterCurrent(result.run)
        },
        onError: showErrorToast,
      },
    )
  const setOwn = (ownStores: boolean) =>
    save.mutate(
      { op: { kind: "setFolderStoresSource", folderId: folder.id, ownStores } },
      { onSuccess: (result) => noteRunAfterCurrent(result.run), onError: showErrorToast },
    )
  return (
    <FilterScopeProvider scopeKey={STORES_SCOPE}>
      <StoresCardBody
        persistKey={`${PACKAGE_CARD_KEY}.folder-stores`}
        assignedIds={assignedIds}
        isSaved={(storeId) => saved.has(storeId)}
        onAssign={(storeIds, checked) => {
          const next = new Set(assignedIds)
          for (const storeId of storeIds) {
            if (checked) next.add(storeId)
            else next.delete(storeId)
          }
          setPicked(next)
        }}
        disabledReason={disabledReason}
        targetLabel="every package in this folder"
        emptyTitle="This folder has no stores yet"
        source={
          parent && (
            <InheritanceSource
              from={following ? parent.name : undefined}
              ownLabel="Own stores"
              actionLabel={following ? "Use own stores" : `Use ${parent.name}'s stores`}
              actionTitle={
                following
                  ? "Give this subfolder its own store list, starting from its parent's"
                  : `Follow ${parent.name}'s stores again`
              }
              disabled={disabledReason !== undefined || save.isPending}
              onAction={() => setOwn(following)}
            />
          )
        }
        footer={
          <InlineAlert
            tone={changed ? "warning" : "neutral"}
            title={`Stores apply to all ${pluralize(packageCount, "package")} in this folder`}
            description="Every package and subfolder inside ends up with exactly these stores, including those with their own store list. Takes effect immediately."
            secondaryAction={
              changed ? { label: "Reset", onClick: () => setPicked(null) } : undefined
            }
            action={{
              label: `Apply to all ${pluralize(packageCount, "package")}`,
              onClick: apply,
              requires: "edit",
              allowedTitle: stagedReason,
              disabled:
                !changed ||
                save.isPending ||
                disabledReason !== undefined ||
                stagedReason !== undefined,
            }}
          />
        }
      />
    </FilterScopeProvider>
  )
}

function FolderPackageRow({ pkg }: { pkg: RangePackage }) {
  const nav = usePackageNavigator()
  const deleting = nav.draft.isDeleting(pkg.id)
  const status = PACKAGE_STATUS_META[pkg.status]
  return (
    <Button
      variant="transparent"
      className="justify-start [&>div]:w-full [&>div]:justify-start [&>div]:gap-2"
      onClick={() => nav.select({ kind: "package", id: pkg.id })}
    >
      <Package size={14} className="shrink-0 text-badge-primary-fg-text" />
      <span className="flex-1 truncate text-left">{pkg.name}</span>
      {deleting && <span className="paragraph-xs text-fg-muted">· deleting on save</span>}
      <span className="paragraph-xs text-fg-muted">{railWindowLabel(pkg)}</span>
      <span
        className={cn(
          "paragraph-xs",
          pkg.storesFolderId === null ? "text-fg-interactive" : "text-fg-muted",
        )}
      >
        {storesLabel(pkg)}
      </span>
      <Badge variant={status.variant}>{status.label}</Badge>
    </Button>
  )
}

/** The folder's packages, then one group per subfolder, each with its own Add package. */
function FolderPackagesCard({
  folder,
  total,
  disabledReason,
}: {
  folder: PackageFolder
  /** Packages in the folder and its subfolders. */
  total: number
  disabledReason: string | undefined
}) {
  const nav = usePackageNavigator()
  const subfolders = nav.folders.filter((other) => other.parentId === folder.id)
  const byFolder = Map.groupBy(nav.packages, (pkg) => pkg.folderId)
  const direct = byFolder.get(folder.id) ?? []
  const addButton = (folderId: string, label: string) => (
    <Button
      variant="transparent"
      size="sm"
      requires="edit"
      allowedTitle={disabledReason}
      disabled={disabledReason !== undefined}
      className="gap-1"
      onClick={() => nav.newPackage(folderId)}
    >
      <Plus size={12} />
      {label}
    </Button>
  )
  return (
    <ExpandableCard
      className="shrink-0"
      persistKey={`${PACKAGE_CARD_KEY}.folder-packages`}
      defaultExpanded
    >
      <ExpandableCardHeader>
        <Package size={16} className="text-fg-muted" />
        <CardTitle meta={pluralize(total, "package")}>Packages</CardTitle>
        <div onClick={(event) => event.stopPropagation()}>
          {addButton(folder.id, "New package")}
        </div>
      </ExpandableCardHeader>
      <ExpandableCardContent className="flex flex-col gap-3">
        {total === 0 && subfolders.length === 0 ? (
          <EmptyState
            icon={Package}
            title={`Nothing in ${folder.name} yet`}
            description="Create a package here, or drag one onto the folder in the list."
            className="py-6"
            action={{
              label: "New package",
              onClick: () => nav.newPackage(folder.id),
              requires: "edit",
              allowedTitle: disabledReason,
              disabled: disabledReason !== undefined,
            }}
          />
        ) : (
          <>
            {direct.length > 0 && (
              <div className="flex flex-col">
                {direct.map((pkg) => (
                  <FolderPackageRow key={pkg.id} pkg={pkg} />
                ))}
              </div>
            )}
            {subfolders.map((sub) => {
              const inSub = byFolder.get(sub.id) ?? []
              return (
                <div key={sub.id} className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 border-b border-border-base pb-1">
                    <Button
                      variant="transparent"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => nav.select({ kind: "folder", id: sub.id })}
                    >
                      <Folder size={14} weight="fill" className="text-badge-primary-fg-text" />
                      {sub.name}
                    </Button>
                    <span className="paragraph-xs text-fg-muted">
                      {pluralize(inSub.length, "package")}
                    </span>
                    <div className="flex-1" />
                    {addButton(sub.id, "Add package")}
                  </div>
                  {inSub.map((pkg) => (
                    <FolderPackageRow key={pkg.id} pkg={pkg} />
                  ))}
                </div>
              )
            })}
          </>
        )}
      </ExpandableCardContent>
    </ExpandableCard>
  )
}

export function PackageFolderPage({ folder }: { folder: PackageFolder }) {
  const nav = usePackageNavigator()
  const { folders, packages } = nav
  const { savePackageFolder } = useAllocationMutations()
  const draft = nav.draft
  const nameRef = useRef<HTMLInputElement>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const parent = folders.find((other) => other.id === folder.parentId)
  const insideIds = packagesInFolder(packages, folders, folder.id)
  const deleting = draft.isFolderDeleting(folder.id)
  const disabledReason = folderDisabledReason(folder, deleting)
  const stagedReason = draft.stagedStoresReason(insideIds)
  const selectFolder = (folderId: string) => nav.select({ kind: "folder", id: folderId })
  usePageRequest(
    { kind: "folder", id: folder.id },
    {
      rename: () => focusAndSelect(nameRef),
      delete: () => setConfirmDelete(true),
    },
  )
  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-6">
      {parent && (
        <Button
          variant="transparent"
          size="sm"
          className="self-start"
          onClick={() => selectFolder(parent.id)}
        >
          {parent.name} ›
        </Button>
      )}
      <div className="flex items-center gap-3">
        <FolderName
          key={`${folder.id}:${folder.name}`}
          folder={folder}
          siblings={folders.filter((other) => other.parentId === folder.parentId)}
          nameRef={nameRef}
          disabledReason={disabledReason}
          save={savePackageFolder}
        />
        {parent && <Badge variant="violet">{`Subfolder of ${parent.name}`}</Badge>}
        {folder.deactivated && <Badge variant="neutral">Archived</Badge>}
        {deleting && <span className="label-sm text-fg-muted">· deleting on save</span>}
        <span className="label-sm text-fg-muted">{pluralize(insideIds.length, "package")}</span>
        <div className="flex-1" />
        <FolderActionsMenu
          folder={folder}
          place="header"
          onRename={() => focusAndSelect(nameRef)}
          onDelete={() => setConfirmDelete(true)}
        />
      </div>
      {confirmDelete && !deleting && (
        <DeletionImpact
          target={{ folderId: folder.id }}
          label={`${folder.name} and everything in it`}
          onCancel={() => setConfirmDelete(false)}
          onMarked={() => setConfirmDelete(false)}
        />
      )}
      <FolderDatesCard
        folder={folder}
        parent={parent}
        disabledReason={disabledReason}
        save={savePackageFolder}
        onSelectFolder={selectFolder}
      />
      <FolderStoresCard
        folder={folder}
        parent={parent}
        packageCount={insideIds.length}
        stagedReason={stagedReason}
        disabledReason={disabledReason}
        save={savePackageFolder}
      />
      <FolderPackagesCard
        folder={folder}
        total={insideIds.length}
        disabledReason={disabledReason}
      />
    </div>
  )
}
